import api from './api';
import { Customer } from './customers';
import { Business } from './businesses';
import { Team } from './teams';
import { User } from './auth';
import { Subject } from './subjects';
import { Staff } from './staffs';
import { TaskType } from './taskTypes';
import { TaskTemplate } from './taskTemplates';
import { Task } from './tasks';

const unwrap = <T>(response: any): T => response.data?.data ?? response.data;

export interface LetterSubTask {
  id: number;
  letter_id: number;
  type: string;
  name: string;
  completed: boolean;
  created_by?: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
  creator?: User;
  updater?: User;
}

export interface LetterItem {
  id: number;
  letter_id: number;
  attachment?: string;
  batch_no?: number;
  approval_status?: 'pending' | 'approved' | 'rejected';
  status?: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  creator?: User;
}

export interface LetterComment {
  id: number;
  letter_id: number;
  user_id?: number;
  comment: string;
  created_at: string;
  user?: User;
}

export interface LetterReview {
  id: number;
  letter_id: number;
  customer_id?: number;
  review: string;
  rating?: number | null;
  created_at: string;
  customer?: Customer;
  creator?: User;
}

export interface Letter {
  id: number;
  type?: 'personal' | 'business';
  reference_id?: number;
  reference_number?: string;
  customer_id?: number;
  business_id?: number;
  sender_id?: number;
  subject_id?: number;
  task_type_id?: number;
  task_template_id?: number;
  task_id?: number;
  create_date?: string;
  due_date?: string;
  team_id?: number;
  status: string;
  is_sent?: boolean;
  enabled: boolean;
  created_by?: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
  customer?: Customer;
  business?: Business;
  sender?: Staff;
  subject?: Subject;
  taskType?: TaskType;
  taskTemplate?: TaskTemplate;
  task?: Task;
  team?: Team;
  subTasks?: LetterSubTask[];
  sub_tasks?: LetterSubTask[];
  items?: LetterItem[];
  comments?: LetterComment[];
  reviews?: LetterReview[];
}

export interface CreateLetterData {
  type?: 'personal' | 'business';
  reference_id?: number;
  customer_id?: number;
  business_id?: number;
  sender_id?: number;
  subject_id?: number;
  task_type_id?: number;
  task_template_id?: number;
  task_id?: number;
  create_date?: string;
  due_date?: string;
  team_id?: number;
  status?: string;
  is_sent?: boolean;
  enabled?: boolean;
  sub_tasks?: Array<{
    id?: number;
    name: string;
    completed?: boolean;
  }>;
}

export interface CreateLetterItemData {
  letter_id: number;
  attachment?: File;
  status?: string;
  enabled?: boolean;
  batch_no?: number;
  approval_status?: 'pending' | 'approved' | 'rejected';
}

export interface CreateLetterCommentData {
  letter_id: number;
  comment: string;
}

export interface CreateLetterReviewData {
  letter_id: number;
  customer_id?: number;
  review: string;
  rating?: number | null;
}

export const letterService = {
  async getAll(params?: { page?: number; per_page?: number }) {
    const response = await api.get('/letters', { params });
    return response.data;
  },

  async getById(id: number): Promise<Letter> {
    const response = await api.get(`/letters/${id}`);
    return unwrap<Letter>(response);
  },

  async create(data: CreateLetterData): Promise<Letter> {
    const response = await api.post('/letters', data);
    return unwrap<Letter>(response);
  },

  async update(id: number, data: Partial<CreateLetterData>): Promise<Letter> {
    const response = await api.put(`/letters/${id}`, data);
    return unwrap<Letter>(response);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/letters/${id}`);
  },
  async approve(id: number): Promise<Letter> {
    const response = await api.post(`/letters/${id}/approve`);
    return unwrap<Letter>(response);
  },
  async import(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/letters/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  async downloadImportTemplate() {
    const response = await api.get('/letters/import/template', {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  async export(payload: { ids?: number[]; select_all?: boolean; excluded_ids?: number[]; filters?: Record<string, any> }) {
    const response = await api.post(
      '/letters/export',
      payload,
      { responseType: 'blob' }
    );
    return response.data as Blob;
  },
  async batchDelete(payload: { ids?: number[]; select_all?: boolean; excluded_ids?: number[]; filters?: Record<string, any> }) {
    const response = await api.post('/letters/batch-delete', payload);
    return response.data;
  },
};

export const letterItemService = {
  async create(data: CreateLetterItemData): Promise<LetterItem> {
    const formData = new FormData();
    formData.append('letter_id', data.letter_id.toString());
    if (data.attachment) {
      formData.append('attachment', data.attachment);
    }
    if (typeof data.batch_no === 'number') {
      formData.append('batch_no', data.batch_no.toString());
    }
    if (data.approval_status) {
      formData.append('approval_status', data.approval_status);
    }
    if (data.status) {
      formData.append('status', data.status);
    }
    formData.append('enabled', (data.enabled ?? true).toString());
    
    const response = await api.post('/letter-items', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap<LetterItem>(response);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/letter-items/${id}`);
  },

  async view(id: number): Promise<Blob> {
    const response = await api.get(`/letter-items/${id}/view`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async download(id: number): Promise<Blob> {
    const response = await api.get(`/letter-items/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

export const letterCommentService = {
  async create(data: CreateLetterCommentData): Promise<LetterComment> {
    const response = await api.post('/letter-comments', data);
    return unwrap<LetterComment>(response);
  },
  async delete(id: number): Promise<void> {
    await api.delete(`/letter-comments/${id}`);
  }
};

export const letterReviewService = {
  async create(data: CreateLetterReviewData): Promise<LetterReview> {
    const response = await api.post('/letter-reviews', data);
    return unwrap<LetterReview>(response);
  }
};
